import { useTranslation } from 'react-i18next';
import { useCreateCard, useCreateCardTemplate } from '../api';
import { useToast } from '@/shared/lib';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { CreateCardFormValues } from './types';
import { DEFAULT_COUNTRY_CODE } from './constants';
import { CARD_NUMBER_INVALID_ERROR_CODE } from '@/entities/card';

type Props = {
  form: UseFormReturn<CreateCardFormValues>;
  onSuccess: () => void;
};

export const useCreateCardFormLogic = ({ form, onSuccess }: Props) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const { mutateAsync: createCardMutation, isPending: isCreatingCardPending } =
    useCreateCard({
      onError: error => {
        if (error.code === CARD_NUMBER_INVALID_ERROR_CODE) {
          form.setError('cardNumber', {
            type: 'validate',
            message: error.message,
          });
          return;
        }
        toast.error(
          error.message || t('savedAddresses.card.creation.result.error')
        );
      },
    });

  const {
    mutateAsync: createCardTemplateMutation,
    isPending: isCreatingCardTemplatePending,
  } = useCreateCardTemplate({
    onSuccess: () => {
      toast.success(t('savedAddresses.card.creation.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.card.creation.result.error')
      );
    },
  });

  const createCard = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();

    const response = await createCardMutation({
      cardNumber: values.cardNumber,
      currencyCode: values.currencyCode,
      cardholderName: values.cardholderName,
      isCardholderCard: values.isCardholderCard,
      countryCode: DEFAULT_COUNTRY_CODE,
    });

    if (!response.userCardId) return;

    await createCardTemplateMutation({
      templateName: values.templateName,
      cardNumber: values.cardNumber,
      cardholderName: values.cardholderName,
      userCardId: response.userCardId,
    });
  }, [form, createCardMutation]);

  return {
    createCard,
    isPending: isCreatingCardPending || isCreatingCardTemplatePending,
  };
};
